export interface GameSession {
  id: string;
  playerName: string;
  finalScore: number;
  scores: Record<string, number>;
  rollsUsed: number[];
  duration: number; // milliseconds
  date: string;
  completedAt: string;
  achievements: string[];
}

export interface CategoryRecord {
  category: string;
  bestScore: number;
  averageScore: number;
  timesUsed: number;
  date: string;
}

export interface DailyStats {
  date: string;
  gamesPlayed: number;
  totalScore: number;
  averageScore: number;
  bestScore: number;
  achievements: number;
}

export interface PlayerStatistics {
  totalGames: number;
  totalScore: number;
  averageScore: number;
  bestScore: number;
  worstScore: number;
  totalPlayTime: number; // milliseconds
  
  // Streaks
  currentStreak: number;
  longestStreak: number;
  lastPlayDate: string;
  
  // Category records
  categoryRecords: Record<string, CategoryRecord>;
  
  // Performance over time
  dailyStats: DailyStats[];
  
  // Advanced stats
  averageRollsPerCategory: number;
  yatzyCount: number;
  bonusCount: number;
  perfectGames: number; // games with no zero scores
  
  // Efficiency metrics
  fastestGame: number; // milliseconds
  mostEfficientGame: string; // game ID with least rolls used
  _mostEfficientAvg?: number; // avg rolls per category for most efficient game
}

class StatisticsManager {
  private readonly STORAGE_KEY = 'yatzy-statistics';
  private readonly SESSIONS_KEY = 'yatzy-game-sessions';
  
  private stats: PlayerStatistics;
  
  constructor() {
    this.stats = this.loadStatistics();
  }
  
  private loadStatistics(): PlayerStatistics {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (error) {
      console.error('Error loading statistics:', error);
    }
    
    return this.getDefaultStats();
  }
  
  private getDefaultStats(): PlayerStatistics {
    return {
      totalGames: 0,
      totalScore: 0,
      averageScore: 0,
      bestScore: 0,
      worstScore: 0,
      totalPlayTime: 0,
      currentStreak: 0,
      longestStreak: 0,
      lastPlayDate: '',
      categoryRecords: {},
      dailyStats: [],
      averageRollsPerCategory: 0,
      yatzyCount: 0,
      bonusCount: 0,
      perfectGames: 0,
      fastestGame: 0,
      mostEfficientGame: '',
      _mostEfficientAvg: undefined
    };
  }
  
  private saveStatistics(): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.stats));
    } catch (error) {
      console.error('Error saving statistics:', error);
    }
  }
  
  private saveGameSession(session: GameSession): void {
    try {
      const sessions = this.getGameSessions();
      sessions.push(session);
      
      // Keep only last 100 sessions to avoid storage bloat
      const recentSessions = sessions.slice(-100);
      localStorage.setItem(this.SESSIONS_KEY, JSON.stringify(recentSessions));
    } catch (error) {
      console.error('Error saving game session:', error);
    }
  }
  
  private getGameSessions(): GameSession[] {
    try {
      const stored = localStorage.getItem(this.SESSIONS_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Error loading game sessions:', error);
      return [];
    }
  }
  
  recordGameSession(session: Omit<GameSession, 'id' | 'completedAt'>): void {
    const now = new Date();
    const today = now.toISOString().split('T')[0];
    
    const fullSession: GameSession = {
      ...session,
      id: `game_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      completedAt: now.toISOString()
    };
    
    // Update basic stats
    this.stats.totalGames += 1;
    this.stats.totalScore += session.finalScore;
    this.stats.averageScore = this.stats.totalScore / this.stats.totalGames;
    this.stats.totalPlayTime += session.duration;
    
    // Update best/worst scores
    if (session.finalScore > this.stats.bestScore) {
      this.stats.bestScore = session.finalScore;
    }
    
    if (this.stats.totalGames === 1 || session.finalScore < this.stats.worstScore) {
      this.stats.worstScore = session.finalScore;
    }
    
    // Update streaks
    this.updateStreaks(today);
    
    // Update category records
    this.updateCategoryRecords(session.scores, session.date);
    
    // Update daily stats
    this.updateDailyStats(fullSession, today);
    
    // Update advanced stats
    this.updateAdvancedStats(fullSession);
    
    // Update fastest game
    if (this.stats.fastestGame === 0 || session.duration < this.stats.fastestGame) {
      this.stats.fastestGame = session.duration;
    }
    
    this.saveGameSession(fullSession);
    this.saveStatistics();
  }
  
  private updateStreaks(today: string): void {
    const lastPlay = this.stats.lastPlayDate;
    
    if (!lastPlay) {
      // First game ever
      this.stats.currentStreak = 1;
      this.stats.longestStreak = 1;
    } else {
      const lastDate = new Date(lastPlay);
      const todayDate = new Date(today);
      const daysDiff = Math.floor((todayDate.getTime() - lastDate.getTime()) / (1000 * 60 * 60 * 24));
      
      if (daysDiff === 0) {
        // Same day, streak continues
      } else if (daysDiff === 1) {
        // Consecutive day
        this.stats.currentStreak += 1;
        if (this.stats.currentStreak > this.stats.longestStreak) {
          this.stats.longestStreak = this.stats.currentStreak;
        }
      } else {
        // Streak broken
        this.stats.currentStreak = 1;
      }
    }
    
    this.stats.lastPlayDate = today;
  }
  
  private updateCategoryRecords(scores: Record<string, number>, date: string): void {
    Object.entries(scores).forEach(([category, score]) => {
      if (score === null || score === undefined) return;
      
      const existing = this.stats.categoryRecords[category];
      
      if (!existing) {
        this.stats.categoryRecords[category] = {
          category,
          bestScore: score,
          averageScore: score,
          timesUsed: 1,
          date
        };
      } else {
        // Update best score
        if (score > existing.bestScore) {
          existing.bestScore = score;
          existing.date = date;
        }
        
        // Update average
        const totalScore = existing.averageScore * existing.timesUsed + score;
        existing.timesUsed += 1;
        existing.averageScore = Math.round(totalScore / existing.timesUsed * 100) / 100;
      }
    });
  }
  
  private updateDailyStats(session: GameSession, today: string): void {
    let dailyStat = this.stats.dailyStats.find(d => d.date === today);
    
    if (!dailyStat) {
      dailyStat = {
        date: today,
        gamesPlayed: 0,
        totalScore: 0,
        averageScore: 0,
        bestScore: 0,
        achievements: 0
      };
      this.stats.dailyStats.push(dailyStat);
    }
    
    dailyStat.gamesPlayed += 1;
    dailyStat.totalScore += session.finalScore;
    dailyStat.averageScore = Math.round(dailyStat.totalScore / dailyStat.gamesPlayed * 100) / 100;
    dailyStat.achievements += session.achievements.length;
    
    if (session.finalScore > dailyStat.bestScore) {
      dailyStat.bestScore = session.finalScore;
    }
    
    // Keep only last 365 days
    this.stats.dailyStats = this.stats.dailyStats
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
      .slice(-365);
  }
  
  private updateAdvancedStats(session: GameSession): void {
    // Update average rolls per category
    const totalRolls = session.rollsUsed.reduce((sum, rolls) => sum + rolls, 0);
    const categoriesUsed = session.rollsUsed.length;
    const gameAverage = categoriesUsed > 0 ? totalRolls / categoriesUsed : 0;
    
    this.stats.averageRollsPerCategory = 
      (this.stats.averageRollsPerCategory * (this.stats.totalGames - 1) + gameAverage) / this.stats.totalGames;
    
    // Count Yatzy
    if (session.scores.yatzy === 50) {
      this.stats.yatzyCount += 1;
    }
    
    // Count bonus
    const upperSection = ['ones', 'twos', 'threes', 'fours', 'fives', 'sixes'];
    const upperScore = upperSection.reduce((sum, key) => sum + (session.scores[key] || 0), 0);
    if (upperScore >= 63) {
      this.stats.bonusCount += 1;
    }
    
    // Check for perfect game (no zeros)
    const hasZero = Object.values(session.scores).some(score => score === 0);
    if (!hasZero) {
      this.stats.perfectGames += 1;
    }
    
    // Check for most efficient game
    const avgRollsThisGame = categoriesUsed > 0 ? totalRolls / categoriesUsed : 3;
    if (!this.stats.mostEfficientGame) {
      this.stats.mostEfficientGame = session.id;
      this.stats._mostEfficientAvg = avgRollsThisGame;
    } else if (avgRollsThisGame < (this.stats._mostEfficientAvg ?? 3)) {
      this.stats.mostEfficientGame = session.id;
      this.stats._mostEfficientAvg = avgRollsThisGame;
    }
  }
  
  getStatistics(): PlayerStatistics {
    return { ...this.stats };
  }
  
  getRecentSessions(limit: number = 10): GameSession[] {
    return this.getGameSessions()
      .sort((a, b) => new Date(b.completedAt).getTime() - new Date(a.completedAt).getTime())
      .slice(0, limit);
  }
  
  getPerformanceTrend(days: number = 30): DailyStats[] {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - days);
    
    return this.stats.dailyStats
      .filter(d => new Date(d.date) >= cutoffDate)
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  }
  
  getBestCategoryScores(): CategoryRecord[] {
    return Object.values(this.stats.categoryRecords)
      .sort((a, b) => b.bestScore - a.bestScore);
  }
  
  resetStatistics(): void {
    this.stats = this.getDefaultStats();
    localStorage.removeItem(this.STORAGE_KEY);
    localStorage.removeItem(this.SESSIONS_KEY);
  }
  
  exportData(): { statistics: PlayerStatistics; sessions: GameSession[] } {
    return {
      statistics: this.getStatistics(),
      sessions: this.getGameSessions()
    };
  }
}

export const statisticsManager = new StatisticsManager();
